<i18n
  src="@/locales/views/service_provider/warehouse/movements/new_movement.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/inventory_movement.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_warehouse_movements')"
    :params="{
      service_provider_id: service_provider_id,
      warehouse_id: warehouse_id,
    }"
  />
  <bread-crumbs :labels="[module_name, warehouse.name]"></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title", { movement_type: movement_type_label }) }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-6">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.inventory_movement.registered_at") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="inventory_movement.registered_at"
                  :invalidClass="isInvalid('registered_at')"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.inventory_movement.inventory_item") }}
              </label>
              <div class="mt-1">
                <select-form
                  :options="inventory_items"
                  v-model="inventory_movement.inventory_item_id"
                  :is_invalid="isInvalidSelect('inventory_item')"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.inventory_movement.quantity") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="inventory_movement.quantity"
                  :class="['input-text', isInvalid('quantity')]"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.inventory_movement.document_number") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="inventory_movement.document_number"
                  :class="['input-text', isInvalid('document_number')]"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.inventory_movement.supplier") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-model="inventory_movement.supplier_id"
                  :options="suppliers"
                  taggable
                  pushTags
                  :createOption="addOption"
                  :is_invalid="isInvalidSelect('supplier')"
                  :reduce="(opt) => opt?.id"
                />
              </div>
            </div>
          </div>
          <div class="pt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-6">
              <label class="label-form">
                {{ $t("model.inventory_movement.comments") }}
              </label>
              <div class="mt-1">
                <textarea
                  v-model="inventory_movement.comments"
                  rows="3"
                  class="input-text-area"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowWarehouse"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="create()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>

  <loading-message v-else />
</template>

<script>
import moment from "moment";
import Requester from "@/views/service_provider/requesters/warehouse/movements/manage_inventory_movements.js";

export default {
  name: "SPWarehouseMovementsNewItem",
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      warehouse: {},
      inventory_items: [],
      suppliers: [],
      inventory_movement: {
        registered_at: null,
        quantity: null,
        comments: "",
        document_number: null,
        inventory_item_id: null,
        supplier_id: null,
      },
    };
  },
  props: {
    service_provider_id: { required: true },
    warehouse_id: { required: true },
    movement_type: { required: true },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          service_provider_id: this.service_provider_id,
        },
      }).then(() => this.loadData())
    );
  },
  computed: {
    movement_type_label: function () {
      return this.movement_type == 0 ? "entrada" : "salida";
    },
    movement_type_value: function () {
      return this.movement_type == 0 ? "inbound" : "outbound";
    },
  },
  methods: {
    loadData: async function () {
      this.inventory_movement.registered_at = moment().format("YYYY-MM-DD");
      this.fetchWarehouse();
      this.fetchInventoryItems();
      this.fetchSuppliers();
      this.load_general_data = true;
    },

    addOption: function (newOption) {
      const optionObj = { id: 0, name: newOption };
      this.inventory_movement.supplier_name = newOption;
      if (this.suppliers.some((supplier) => supplier.id == 0)) {
        let supplier = this.suppliers.find((supplier) => supplier.id == 0);
        supplier.name = newOption;
      } else {
        this.suppliers.push(optionObj);
      }
      this.inventory_movement.supplier_id = 0;
    },

    fetchWarehouse: async function () {
      let response = await Requester.fetchWarehouse(
        this.service_provider_id,
        this.warehouse_id
      );

      this.warehouse = response.data;
    },

    fetchInventoryItems: async function () {
      let response = await Requester.fetchInventoryItems(
        this.service_provider_id,
        this.warehouse_id
      );

      this.inventory_items = response.data;
    },

    fetchSuppliers: async function () {
      let response = await Requester.fetchSuppliers(
        this.service_provider_id,
        this.warehouse_id
      );

      this.suppliers = response.data;
    },

    create: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.create(
          this.service_provider_id,
          this.warehouse_id,
          {
            ...this.inventory_movement,
            movement_type: this.movement_type_value,
          }
        );

        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowWarehouse();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowWarehouse: function () {
      this.$router.push({
        name: this.routeName("show_warehouse_movements"),
        params: {
          service_provider_id: this.service_provider_id,
          warehouse_id: this.warehouse_id,
        },
      });
    },
  },
  watch: {
    "inventory_movement.supplier_id": function (newValue) {
      if (newValue != 0) {
        this.inventory_movement.supplier_name = "";
      } else {
        this.inventory_movement.supplier_name = this.suppliers.find(
          (supplier) => supplier.id === newValue
        ).name;
      }
    },
  },
};
</script>
